/**
 * Admin-lite API klient (WP8). Zrcadlo schémat v
 * `apps/api/src/kabinet/api/v1/admin.py`. Autentizace přes Bearer token
 * uložený v `localStorage` (+ HttpOnly cookie nastavená serverem).
 */

import { ApiError } from './api';
import type {
  ChalkColor,
  ContentType,
  FulfillmentType,
  OrderStatus,
  PaymentMethod,
  ProductStatus,
  SubjectRef,
} from './api';

const ADMIN_BASE = '/api/v1/admin';
const TOKEN_KEY = 'kabinet_admin_token';

/* Token */

export function getAdminToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function setAdminToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* localStorage nedostupné, cookie stále funguje */
  }
}

function clearAdminToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function isLoggedIn(): boolean {
  return Boolean(getAdminToken());
}

/* Fetch vrstva */

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json', ...extra };
  const token = getAdminToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function parseError(res: Response): Promise<never> {
  let detail = `Chyba serveru (${res.status}).`;
  try {
    const data = (await res.json()) as { detail?: unknown };
    if (typeof data?.detail === 'string') detail = data.detail;
    else if (Array.isArray(data?.detail) && data.detail.length > 0) {
      const first = data.detail[0] as { msg?: string };
      if (first?.msg) detail = first.msg;
    }
  } catch {
    /* tělo není JSON */
  }
  throw new ApiError(res.status, detail);
}

async function adminFetch<T>(
  method: string,
  path: string,
  opts: { body?: unknown; form?: FormData } = {},
): Promise<T> {
  let res: Response;
  const init: RequestInit = { method, credentials: 'same-origin' };
  if (opts.form) {
    init.headers = authHeaders();
    init.body = opts.form;
  } else if (opts.body !== undefined) {
    init.headers = authHeaders({ 'Content-Type': 'application/json' });
    init.body = JSON.stringify(opts.body);
  } else {
    init.headers = authHeaders();
  }

  try {
    res = await fetch(`${ADMIN_BASE}${path}`, init);
  } catch {
    throw new ApiError(0, 'Nepodařilo se spojit se serverem.');
  }

  // Vypršelá / neplatná session → odhlásit a na přihlášení.
  if (res.status === 401) {
    clearAdminToken();
    if (!path.startsWith('/login')) {
      window.location.assign('/admin/prihlaseni');
    }
    return parseError(res);
  }
  if (!res.ok) return parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/* Typy */

export type AdminLogin = { token: string; expires_at: string };

export type AdminProductListItem = {
  id: number;
  slug: string;
  title: string;
  status: ProductStatus;
  price_czk: number;
  subject: SubjectRef;
  content_type: ContentType;
  fulfillment_type: FulfillmentType;
  has_sample: boolean;
  asset_count: number;
  lesson_count: number;
  license_free: number;
  license_total: number;
};

export type AdminAsset = {
  id: number;
  kind: 'FULL' | 'SAMPLE' | 'GALLERY';
  filename: string;
  mime: string;
  size_bytes: number | null;
  sort_order: number;
};

export type AdminLesson = {
  id: number;
  title: string;
  order_index: number;
  duration_s: number;
  is_sample: boolean;
};

export type AdminLicenseKey = {
  id: number;
  key_value: string;
  state: 'FREE' | 'ASSIGNED';
  assigned_order_id: number | null;
};

export type AdminProductDetail = {
  id: number;
  slug: string;
  title: string;
  subtitle: string | null;
  description_md: string;
  price_czk: number;
  status: ProductStatus;
  subject: SubjectRef;
  content_type: ContentType;
  fulfillment_type: FulfillmentType;
  grade: string | null;
  page_count: number | null;
  duration_s: number | null;
  has_sample: boolean;
  sample_note: string | null;
  created_at: string;
  updated_at: string;
  category_slugs: string[];
  assets: AdminAsset[];
  lessons: AdminLesson[];
  license_keys: AdminLicenseKey[];
};

export type ProductCreate = {
  slug: string;
  title: string;
  subtitle?: string | null;
  description_md?: string;
  price_czk: number;
  /** Slug předmětu z číselníku (např. `matematika`). */
  subject: string;
  content_type: ContentType;
  fulfillment_type: FulfillmentType;
  grade?: string | null;
  page_count?: number | null;
  duration_s?: number | null;
  has_sample?: boolean;
  sample_note?: string | null;
  category_slugs?: string[];
};

export type ProductUpdate = Partial<Omit<ProductCreate, 'slug'> & { status: ProductStatus }>;

export type PaymentState = 'PENDING' | 'CONFIRMED' | 'FAILED';

export type AdminOrderListItem = {
  id: number;
  public_token: string;
  email: string;
  product_title: string;
  product_slug: string;
  amount_czk: number;
  status: OrderStatus;
  payment_method: PaymentMethod | null;
  payment_state: PaymentState | null;
  fulfillment_type: FulfillmentType;
  created_at: string;
  paid_at: string | null;
  delivered_at: string | null;
  has_note: boolean;
};

export type AdminPayment = {
  id: number;
  method: PaymentMethod;
  state: PaymentState;
  amount_czk: number;
  vs: string | null;
  account: string | null;
  comgate_ref: string | null;
  created_at: string;
  confirmed_at: string | null;
};

export type AdminFioTx = {
  id: number;
  fio_id: string;
  amount_czk: number;
  vs: string | null;
  counter_account: string | null;
  received_at: string;
};

export type AdminOrderDetail = {
  id: number;
  public_token: string;
  email: string;
  variant: string | null;
  product_title: string;
  product_slug: string;
  amount_czk: number;
  status: OrderStatus;
  payment_method: PaymentMethod | null;
  variable_symbol: string;
  fulfillment_type: FulfillmentType;
  consent_terms: boolean;
  consent_digital_delivery: boolean;
  created_at: string;
  updated_at: string;
  paid_at: string | null;
  delivered_at: string | null;
  expires_at: string | null;
  delivery_note: string | null;
  payments: AdminPayment[];
  fio_transactions: AdminFioTx[];
};

/* Nastavení (WP9 konfigurační UI) */

export type AdminSettingType = 'text' | 'number' | 'bool' | 'secret';

export type AdminSettingField = {
  key: string;
  label: string;
  type: AdminSettingType;
  help: string;
  is_secret: boolean;
  /** Efektivní hodnota (u tajemství vždy null). */
  value: string | number | boolean | null;
  /** Jen u tajemství: je hodnota nastavená? Jinak null. */
  is_set: boolean | null;
};

export type AdminSettingGroup = {
  key: string;
  title: string;
  help: string;
  fields: AdminSettingField[];
};

export type AdminSettings = { groups: AdminSettingGroup[] };

export type AdminSettingsUpdate = {
  values: Record<string, string | number | boolean>;
  clear_secrets?: string[];
};

/* Taxonomie: kategorie a předměty (WP11) */

/** Plochý řádek stromu kategorií z `GET /admin/categories`. */
export type AdminCategory = {
  id: number;
  slug: string;
  name: string;
  parent_id: number | null;
  sort_order: number;
  icon: string;
  description: string | null;
  /** Počet přiřazených produktů přes VŠECHNY stavy (i koncepty). */
  product_count: number;
};

export type CategoryCreate = {
  name: string;
  slug?: string;
  parent_id?: number | null;
  icon?: string;
  description?: string | null;
};

export type CategoryUpdate = Partial<CategoryCreate>;

/** Řádek číselníku předmětů z `GET /admin/subjects`. */
export type AdminSubject = {
  id: number;
  slug: string;
  name: string;
  icon: string;
  color: ChalkColor;
  sort_order: number;
  product_count: number;
};

export type SubjectCreate = {
  name: string;
  slug?: string;
  icon?: string;
  color?: ChalkColor;
};

export type SubjectUpdate = Partial<SubjectCreate>;

/* API */

export const adminApi = {
  async login(password: string): Promise<AdminLogin> {
    const data = await adminFetch<AdminLogin>('POST', '/login', { body: { password } });
    setAdminToken(data.token);
    return data;
  },
  async logout(): Promise<void> {
    try {
      await adminFetch<void>('POST', '/logout');
    } finally {
      clearAdminToken();
    }
  },
  session: () => adminFetch<{ authenticated: boolean }>('GET', '/session'),

  listProducts: (params: { status?: ProductStatus; q?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.q) qs.set('q', params.q);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return adminFetch<AdminProductListItem[]>('GET', `/products${suffix}`);
  },
  getProduct: (id: number) => adminFetch<AdminProductDetail>('GET', `/products/${id}`),
  createProduct: (body: ProductCreate) =>
    adminFetch<AdminProductDetail>('POST', '/products', { body }),
  updateProduct: (id: number, body: ProductUpdate) =>
    adminFetch<AdminProductDetail>('PATCH', `/products/${id}`, { body }),
  uploadAsset: (id: number, kind: AdminAsset['kind'], file: File) => {
    const form = new FormData();
    form.set('kind', kind);
    form.set('file', file);
    return adminFetch<AdminAsset>('POST', `/products/${id}/assets`, { form });
  },
  deleteAsset: (id: number) => adminFetch<void>('DELETE', `/assets/${id}`),
  addLesson: (
    productId: number,
    body: { title: string; duration_s?: number; order_index?: number; is_sample?: boolean },
  ) => adminFetch<AdminLesson>('POST', `/products/${productId}/lessons`, { body }),
  updateLesson: (
    id: number,
    body: Partial<{ title: string; duration_s: number; order_index: number; is_sample: boolean }>,
  ) => adminFetch<AdminLesson>('PATCH', `/lessons/${id}`, { body }),
  deleteLesson: (id: number) => adminFetch<void>('DELETE', `/lessons/${id}`),
  addLicenseKeys: (productId: number, keys: string[]) =>
    adminFetch<{ added: number; skipped: number }>('POST', `/products/${productId}/license-keys`, {
      body: { keys },
    }),

  listOrders: (params: { status?: OrderStatus; q?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.q) qs.set('q', params.q);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return adminFetch<AdminOrderListItem[]>('GET', `/orders${suffix}`);
  },
  getOrder: (id: number) => adminFetch<AdminOrderDetail>('GET', `/orders/${id}`),
  resendDelivery: (id: number) =>
    adminFetch<AdminOrderDetail>('POST', `/orders/${id}/resend-delivery`),
  markPaid: (id: number, note?: string) =>
    adminFetch<AdminOrderDetail>('POST', `/orders/${id}/mark-paid`, {
      body: { note: note ?? null },
    }),
  cancelOrder: (id: number, note?: string) =>
    adminFetch<AdminOrderDetail>('POST', `/orders/${id}/cancel`, { body: { note: note ?? null } }),

  getSettings: () => adminFetch<AdminSettings>('GET', '/settings'),
  updateSettings: (body: AdminSettingsUpdate) =>
    adminFetch<AdminSettings>('PUT', '/settings', { body }),

  /* Kategorie */
  listCategories: () => adminFetch<AdminCategory[]>('GET', '/categories'),
  createCategory: (body: CategoryCreate) =>
    adminFetch<AdminCategory>('POST', '/categories', { body }),
  updateCategory: (id: number, body: CategoryUpdate) =>
    adminFetch<AdminCategory>('PATCH', `/categories/${id}`, { body }),
  deleteCategory: (id: number) => adminFetch<void>('DELETE', `/categories/${id}`),
  reorderCategories: (parentId: number | null, orderedIds: number[]) =>
    adminFetch<void>('POST', '/categories/reorder', {
      body: { parent_id: parentId, ordered_ids: orderedIds },
    }),

  /* Předměty */
  listSubjects: () => adminFetch<AdminSubject[]>('GET', '/subjects'),
  createSubject: (body: SubjectCreate) => adminFetch<AdminSubject>('POST', '/subjects', { body }),
  updateSubject: (id: number, body: SubjectUpdate) =>
    adminFetch<AdminSubject>('PATCH', `/subjects/${id}`, { body }),
  deleteSubject: (id: number) => adminFetch<void>('DELETE', `/subjects/${id}`),
  reorderSubjects: (orderedIds: number[]) =>
    adminFetch<void>('POST', '/subjects/reorder', { body: { ordered_ids: orderedIds } }),
};
